import { FieldRowsAdvanced, emptyFieldRow } from '@components/Forms/FieldRows';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';
import {
	stringRequiredValidationScheme,
	urlValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import {
	FLOW_ACTIONS_INFO,
	HiddenActionInputs,
	IFlowsMetadata,
	IFlowTaskInput,
	IVariable,
	Task,
	TaskInputType
} from 'types/flows';
import { array, object, string } from 'yup';

interface IFlowTaskDialogProps {
	onDone: (task: Task) => void;
	task: Task;
	onCancel: () => void;
	metadata?: IFlowsMetadata;
}

const getVariableValue = (v: IVariable, action?: IFlowTaskInput) => {
	const { value } = v;
	if (action?.required && action?.visible && !value) {
		switch (action?.type) {
			case TaskInputType.object:
				return [emptyFieldRow];
			case TaskInputType.boolean:
				return false;
			case TaskInputType.number:
				return 0;
			default:
				return '';
		}
	}
	return value;
};

const FlowTaskDialog = ({
	task,
	metadata,
	onDone,
	onCancel
}: IFlowTaskDialogProps) => {
	const currentAction = metadata?.actions[task.action!];
	const values = useMemo(
		() =>
			Object.keys(task.arguments || {}).reduce(
				(prev, curr) => ({
					...prev,
					[curr]: getVariableValue(
						task.arguments![curr],
						currentAction?.inputs?.find((i) => i.name === curr)
					)
				}),
				{}
			),
		[task.arguments, currentAction]
	);

	const getValidationScheme = (v: IFlowTaskInput) => {
		if (v.name === 'uri') {
			const validation = urlValidationScheme();
			return v.required ? validation.required() : validation;
		}

		switch (v.type) {
			case TaskInputType.object: {
				let scheme = array();
				if (v.required) {
					scheme = scheme.min(1).required();
				}
				return scheme.of(
					object().shape({
						key: stringRequiredValidationScheme(),
						type: stringRequiredValidationScheme()
					})
				);
			}
			default:
				if (v.required) return stringRequiredValidationScheme();
				return string();
		}
	};

	const formik = useFormik<Record<string, any> & { taskName: string }>({
		initialValues: { ...values, taskName: task.name },
		validateOnChange: true,
		validationSchema: object({
			taskName: stringRequiredValidationScheme(),
			...currentAction?.inputs?.reduce(
				(prev, curr) => ({
					...prev,
					...(curr.visible && !(curr.name in HiddenActionInputs)
						? { [curr.name]: getValidationScheme(curr) }
						: {})
				}),
				{}
			)
		}),
		onSubmit: (newValues) => {
			onDone({
				...task,
				arguments: Object.keys(task.arguments || {}).reduce(
					(prev, curr) => ({
						...prev,
						[curr]: { ...task.arguments![curr], value: newValues[curr] }
					}),
					{}
				),
				name: newValues.taskName
			});
		}
	});

	const actions = useMemo(
		() =>
			currentAction?.inputs?.map((input) => {
				if (!input.visible) return null;

				let comp = null;
				switch (input.type) {
					case TaskInputType.object:
						comp = (
							<FieldRowsAdvanced
								name={input.name}
								formik={formik}
								required={input.required}
								onChange={(rows) => formik.setFieldValue(input.name, rows)}
								rows={formik.values[input.name]}
								sx={{ mt: 2 }}
							/>
						);
						break;
					default:
						comp = (
							<TextFieldValidation
								id={input.name}
								formik={formik}
								fullWidth
								required={!!task.arguments?.[input.name]?.required}
								label={input?.displayName || input.name}
							/>
						);
				}

				return (
					<Grid
						item
						xs={12}
						pb={2}
						key={`input-${input.name}`}
						data-testid={`flow-task-input-${input.name}`}
						sx={{
							...(input.name in HiddenActionInputs ? { display: 'none' } : {})
						}}
					>
						{comp}
					</Grid>
				);
			}),
		[currentAction?.inputs, formik, task.arguments]
	);

	const maxWidth = useMemo(
		() =>
			currentAction?.inputs?.some(
				(i) => i.type === TaskInputType.object && i.visible
			)
				? 'sm'
				: 'xs',
		[currentAction?.inputs]
	);

	return (
		<Dialog open maxWidth={maxWidth} disableRestoreFocus>
			<DialogTitle>{task.name}</DialogTitle>
			<DialogContent>
				{currentAction?.displayName && (
					<Typography variant="body1" pb={1}>{`${intl.tr(
						'flows.builder.task.dialog.action.name'
					)} ${currentAction?.displayName}`}</Typography>
				)}
				{currentAction?.description && (
					<Typography variant="body2" color="text.secondary" pb={2}>
						{currentAction?.description}
					</Typography>
				)}
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item container xs={12}>
							<Grid item xs={12} pb={2}>
								<TextFieldValidation
									id="taskName"
									formik={formik}
									required
									fullWidth
									autoFocus
									label={intl.tr('flows.builder.task.dialog.task.name')}
								/>
							</Grid>
							{FLOW_ACTIONS_INFO.includes(currentAction?.name || '') && (
								<Grid item xs={12}>
									<Alert
										severity="info"
										data-testid={`${currentAction?.name}-info`}
									>
										{intl.tr(
											`flows.builder.task.dialog.info.${currentAction?.name}`
										)}
									</Alert>
								</Grid>
							)}
							{actions}
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions sx={{ pr: 2, pb: 2 }}>
				<Button onClick={onCancel} data-testid="task-dialog-cancel-button">
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					disabled={!formik.isValid}
					loading={formik.isSubmitting}
					onClick={() => formik.handleSubmit()}
					data-testid="task-dialog-done-button"
				>
					{intl.tr('button.done')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

FlowTaskDialog.defaultProps = {
	metadata: undefined
};

export default FlowTaskDialog;
